<template>
  <v-container>
    <v-row>
      <v-col cols="12" class="d-flex justify-space-between align-center">
        <h1 class="text-h4">{{ $t('users.title') }}</h1>
        <v-btn color="primary" prepend-icon="mdi-account-plus" @click="openCreateDialog">
          {{ $t('users.create') }}
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card>
          <v-data-table
            :headers="headers"
            :items="users"
            :loading="loading"
            class="elevation-1"
          >
            <template v-slot:item.role="{ item }">
              <v-chip :color="getRoleColor(item.role)" size="small" label>
                {{ item.role }}
              </v-chip>
            </template>
            <template v-slot:item.createdAt="{ item }">
              {{ formatDate(item.createdAt) }}
            </template>
            <template v-slot:item.actions="{ item }">
              <v-icon size="small" class="mr-2" @click="editUser(item)">
                mdi-pencil
              </v-icon>
              <v-icon size="small" color="error" @click="confirmDelete(item)" :disabled="item.role === 'superadmin'">
                mdi-delete
              </v-icon>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="dialog" max-width="500px">
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ isEditing ? $t('users.edit') : $t('users.create') }}</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="valid">
            <v-text-field
              v-model="editedUser.username"
              :label="$t('users.username')"
              :disabled="isEditing"
              required
            ></v-text-field>
            <v-text-field
              v-model="editedUser.password"
              :label="$t('users.password')"
              type="password"
              required
            ></v-text-field>
            <v-select
              v-model="editedUser.role"
              :items="roles"
              :label="$t('users.role')"
              required
            ></v-select>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="closeDialog">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" variant="text" @click="saveUser" :disabled="!valid">{{ $t('common.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="text-h5">{{ $t('users.deleteConfirmTitle') }}</v-card-title>
        <v-card-text>{{ $t('users.deleteConfirmMessage', { name: userToDelete?.username }) }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="deleteDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="error" variant="text" @click="deleteUser">{{ $t('common.delete') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { apiCall } from '../utils/api.js'
import { useAuthStore } from '../stores/auth'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const authStore = useAuthStore()

const users = ref([])
const loading = ref(false)
const dialog = ref(false)
const deleteDialog = ref(false)
const valid = ref(false)
const isEditing = ref(false)
const userToDelete = ref(null)

const editedUser = ref({
  username: '',
  password: '',
  role: 'user'
})

const roles = ['admin', 'finance', 'user']

const headers = [
  { title: t('users.username'), key: 'username' },
  { title: t('users.role'), key: 'role' },
  { title: t('users.createdAt'), key: 'createdAt' },
  { title: t('common.actions'), key: 'actions', sortable: false }
]

const loadUsers = async () => {
  loading.value = true
  try {
    const response = await apiCall('/api/users')
    users.value = response
  } catch (error) {
    console.error('Error loading users:', error)
  } finally {
    loading.value = false
  }
}

const openCreateDialog = () => {
  isEditing.value = false
  editedUser.value = { username: '', password: '', role: 'user' }
  dialog.value = true
}

const editUser = (user) => {
  isEditing.value = true
  editedUser.value = { ...user, password: '' }
  dialog.value = true
}

const closeDialog = () => {
  dialog.value = false
}

const saveUser = async () => {
  try {
    if (isEditing.value) {
      await apiCall(`/api/users/${editedUser.value.username}`, {
        method: 'PUT',
        body: JSON.stringify(editedUser.value)
      })
    } else {
      await apiCall('/api/users', {
        method: 'POST',
        body: JSON.stringify(editedUser.value)
      })
    }
    dialog.value = false
    loadUsers()
  } catch (error) {
    console.error('Error saving user:', error)
    alert(error.response?.data?.message || error.message || 'Error saving user')
  }
}

const confirmDelete = (user) => {
  userToDelete.value = user
  deleteDialog.value = true
}

const deleteUser = async () => {
  try {
    await apiCall(`/api/users/${userToDelete.value.username}`, {
      method: 'DELETE'
    })
    deleteDialog.value = false
    loadUsers()
  } catch (error) {
    console.error('Error deleting user:', error)
  }
}

const getRoleColor = (role) => {
  switch (role) {
    case 'superadmin': return 'purple'
    case 'admin': return 'primary'
    case 'finance': return 'success'
    default: return 'grey'
  }
}

const formatDate = (date) => {
  if (!date) return '-'
  return new Date(date).toLocaleDateString()
}

onMounted(loadUsers)
</script>
